import { useCallback } from 'react'

import { useLocalStorage } from '@buffer-mono/popcorn'

import { useSavedRepliesQuery } from '../savedReplies/useSavedRepliesQuery'

// Threshold for showing the saved replies hint coachmark
const REPLIES_THRESHOLD = 5

// LocalStorage keys
const TOTAL_REPLIES_COUNT_KEY = 'saved-replies-coachmark-total-replies'
const COACHMARK_DISMISSED_KEY = 'saved-replies-coachmark-dismissed'

/**
 * Increments the total replies count in localStorage for saved replies coachmark tracking.
 *
 * Call this whenever a user successfully replies to a comment.
 * Used to determine when to show the saved replies hint coachmark.
 */
export const incrementSavedRepliesCoachmarkCount = (): void => {
  try {
    const currentCount = parseInt(
      localStorage.getItem(TOTAL_REPLIES_COUNT_KEY) ?? '0',
      10,
    )
    localStorage.setItem(TOTAL_REPLIES_COUNT_KEY, String(currentCount + 1))
  } catch {
    // Silently fail if localStorage is not available
  }
}

type UseSavedRepliesCoachmarkReturn = {
  shouldShowCoachmark: boolean
  totalRepliesCount: number
  onDismissCoachmark: () => void
}

/**
 * Hook for managing the saved replies hint coachmark.
 *
 * Shows a coachmark after the user has replied to 5 comments (cumulative, not session-based)
 * to encourage them to use saved replies feature.
 *
 * Conditions for showing:
 * - User has replied to at least 5 comments (total, across all sessions)
 * - User has NOT created any saved replies yet
 * - User has NOT dismissed the coachmark before
 *
 * Note: Reply count is automatically tracked via `incrementSavedRepliesCoachmarkCount()`
 * in `useReplyToComment` - no manual tracking needed.
 *
 * @returns Object containing:
 * - `shouldShowCoachmark`: Whether to show the saved replies coachmark
 * - `totalRepliesCount`: Total number of replies the user has made
 * - `onDismissCoachmark`: Call when user dismisses the coachmark
 *
 * @example
 * ```tsx
 * const { shouldShowCoachmark, onDismissCoachmark } = useSavedRepliesCoachmark()
 *
 * // Show coachmark when conditions are met
 * {shouldShowCoachmark && (
 *   <SavedRepliesCoachmark onDismiss={onDismissCoachmark} />
 * )}
 * ```
 */
export const useSavedRepliesCoachmark = (): UseSavedRepliesCoachmarkReturn => {
  const [totalRepliesCount] = useLocalStorage<number>(
    TOTAL_REPLIES_COUNT_KEY,
    0,
  )
  const [hasBeenDismissed, setHasBeenDismissed] = useLocalStorage<boolean>(
    COACHMARK_DISMISSED_KEY,
    false,
  )

  const { savedReplies, loading } = useSavedRepliesQuery()

  // Derive coachmark visibility from current state
  const hasSavedReplies = (savedReplies?.length ?? 0) > 0
  const hasEnoughReplies = totalRepliesCount >= REPLIES_THRESHOLD
  const shouldShowCoachmark =
    !loading && hasEnoughReplies && !hasSavedReplies && !hasBeenDismissed

  const onDismissCoachmark = useCallback(() => {
    setHasBeenDismissed(true)
  }, [setHasBeenDismissed])

  return {
    shouldShowCoachmark,
    totalRepliesCount,
    onDismissCoachmark,
  }
}
